<template>
	<v-row v-if="!showPickTicketPrintPreview" class="full-height commit-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="commit-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="4" md="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Style, Description, Order #, Organization"
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="commit-search"
								@input="offset = 0"
							></v-text-field>
						</v-col>

						<!-- Filters -->
						<v-col cols="auto">
							<v-checkbox
								class="mt-0 pt-0"
								v-model="searchForm.partialCommited"
								label="Partial Committed"
								color="gray"
								hide-details
							></v-checkbox>
						</v-col>
						<v-col cols="auto">
							<v-checkbox
								class="mt-0 pt-0"
								v-model="searchForm.fillable"
								label="Fillable Items"
								color="gray"
								hide-details
							></v-checkbox>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Action Buttons -->
						<v-col cols="auto">
							<v-btn
								:disabled="
									!selectedItems ||
									(selectedItems && selectedItems.length === 0) ||
									!canDecommit ||
									!hasCommitPermissions()
								"
								depressed
								outlined
								class="mr-2"
								@click="commitData(false)"
							>
								De-Commit
							</v-btn>
							<v-btn
								:disabled="
									!selectedItems ||
									(selectedItems && selectedItems.length === 0) ||
									!canCommit ||
									!hasCommitPermissions()
								"
								depressed
								outlined
								@click="commitData(true)"
							>
								Commit
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 commit-table-container">
			<div class="receiving-table commit-table">
				<div class="flex-table" role="table-head">
					<div class="flex-table-row" role="row-group">
						<div class="flex-table-cell" role="column-header">&nbsp;</div>
						<div
							v-for="header in headers"
							:key="header.value"
							class="flex-table-cell sortable-header"
							:class="{
								'sortable-active': sortBy === header.value,
								'sortable-desc': sortBy === header.value && sortDesc,
							}"
							role="column-header"
							@click="toggleSort(header.value)"
						>
							{{ header.text }}
							<v-icon small class="sort-icon">mdi-arrow-up</v-icon>
						</div>
						<div class="flex-table-cell" role="column-header">&nbsp;</div>
					</div>
					<v-progress-linear
						:active="$apollo.queries.fillableOrders.loading"
						indeterminate
						absolute
						bottom
						height="2"
						color="primary"
					></v-progress-linear>
				</div>
				<div class="flex-table" role="table-body">
					<div v-if="loading" class="loading-overlay">
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
					</div>
					<div v-for="(data, i) in fillableOrders" :key="data.lineItemId" class="flex-table-row" role="row-group">
						<div class="flex-table-cell" role="cell">
							<v-checkbox
								class="form-checkbox-center"
								v-model="data.selected"
								color="accent"
								hide-details="auto"
								@change="
									selectAllSizes(data, () => {
										data.selected ? (showDetailsByRow = i) : (showDetailsByRow = null);
									})
								"
								:disabled="shouldDisableCheckbox(data, selectedStatus) || !hasCommitPermissions()"
							></v-checkbox>
						</div>
						<div class="flex-table-cell" role="cell">
							<SystemCategoryIcon :system-category-id="data.systemCategoryId" />
							{{ data.styleCode }}
						</div>
						<div class="flex-table-cell" role="cell">{{ data.description }}</div>
						<div class="flex-table-cell" role="cell">{{ data.lineNumber }}</div>
						<div class="flex-table-cell" role="cell">{{ data.totalOrdered }}</div>
						<div class="flex-table-cell" role="cell">{{ data.totalOrderQuantityCommitted }}</div>
						<div class="flex-table-cell" role="cell">{{ data.totalAvailable }}</div>
						<div class="flex-table-cell" role="cell">
							<router-link :to="{ path: `/sales-orders/orders/${data.salesOrderId}` }">
								{{ data.orderNumber }}
							</router-link>
						</div>
						<div class="flex-table-cell" role="cell">{{ formatDate(data.orderDate) }}</div>
						<div class="flex-table-cell" role="cell">{{ data.organization }}</div>
						<div class="flex-table-cell" role="cell">
							<div class="receiving-table-status" :class="renderStatusColor(data.status)">
								{{ data.status }}
							</div>
						</div>
						<div class="flex-table-cell" role="cell">
							<v-btn
								icon
								@click="showDetailsByRow == i ? (showDetailsByRow = null) : (showDetailsByRow = i)"
								:disabled="shouldDisableCheckbox(data, selectedStatus)"
							>
								<v-icon>mdi-chevron-down</v-icon>
							</v-btn>
						</div>

						<!-- dropdown sizing -->
						<ExpandTransition>
							<div
								v-if="showDetailsByRow == i"
								class="receiving-table-dropdown"
								:class="showDetailsByRow == i ? 'receiving-table-dropdown-active' : null"
							>
								<div class="receiving-size-chart">
									<div class="flex-table" role="table-head">
										<div class="flex-table-row" role="row-group">
											<div class="flex-table-cell" role="cell">&nbsp;</div>
											<div class="flex-table-cell" role="cell">Size</div>
											<div class="flex-table-cell" role="cell">Ordered</div>
											<div class="flex-table-cell" role="cell">Committed</div>
											<div class="flex-table-cell" role="cell">
												Available
												<v-btn class="ml-2" icon @click="editAvailable = !editAvailable">
													<v-icon v-if="!editAvailable">mdi-pencil</v-icon>
													<v-icon v-else>mdi-check</v-icon>
												</v-btn>
											</div>
											<div class="flex-table-cell" role="cell">Status</div>
										</div>
									</div>
									<div class="flex-table" role="table-body">
										<div
											v-for="(size, i) in data.sizes"
											:key="i"
											class="flex-table-row"
											role="row-group"
										>
											<div class="flex-table-cell" role="cell">
												<v-checkbox
													class="form-checkbox-center"
													v-model="size.selected"
													color="accent"
													hide-details="auto"
													@change="updateSelection"
													:disabled="!hasCommitPermissions()"
												></v-checkbox>
											</div>
											<div class="flex-table-cell" role="cell">
												<div class="form-input">
													<v-text-field
														solo
														flat
														dense
														hide-details="auto"
														v-model="size.size"
														readonly
													></v-text-field>
												</div>
											</div>
											<div class="flex-table-cell" role="cell">
												<div class="form-input">
													<v-text-field
														solo
														flat
														dense
														hide-details="auto"
														v-model="size.ordered"
														readonly
													></v-text-field>
												</div>
											</div>
											<div class="flex-table-cell" role="cell">
												<div class="form-input">
													<v-text-field
														solo
														flat
														dense
														hide-details="auto"
														v-model="size.orderQuantityCommitted"
														readonly
													></v-text-field>
												</div>
											</div>
											<div class="flex-table-cell" role="cell">
												<div class="form-input">
													<v-text-field
														v-if="!editAvailable"
														solo
														flat
														dense
														hide-details="auto"
														v-model="size.available"
														readonly
													></v-text-field>
													<v-text-field
														v-else
														outlined
														dense
														hide-details="auto"
														v-model="size.available"
													></v-text-field>
												</div>
											</div>
											<div class="flex-table-cell" role="cell">
												<div class="form-input">
													<div
														class="receiving-table-status"
														:class="renderStatusColor(size.status)"
													>
														{{ size.status }}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</ExpandTransition>
					</div>
				</div>
			</div>
			<div class="table-controls">
				<v-btn :disabled="!hasPreviousPage" depressed small @click="offset -= 15"> Previous </v-btn>
				<v-btn :disabled="!hasNextPage" depressed small @click="offset += 15"> Next Page </v-btn>
			</div>
		</v-col>
	</v-row>

	<PickTicketPrintTemplate
		v-else-if="showPickTicketPrintPreview"
		:orderId="orderId"
		:pickTicketIds="pickTicketIds"
		@cancel="
			() => {
				$apollo.queries.fillableOrders.refetch();
				showPickTicketPrintPreview = false;
			}
		"
	/>
</template>

<script>
import gql from "graphql-tag";
import ExpandTransition from "@/components/ExpandTransition";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import PickTicketPrintTemplate from "@/components/PickTicketPrintTemplateOptimized.vue";
import { hasPermission, SystemPermissions } from "@/models/User";

export default {
	name: "Commit",
	components: {
		ExpandTransition,
		SystemCategoryIcon,
		PickTicketPrintTemplate,
	},
	data() {
		return {
			searchForm: {
				keywords: "",
				partialCommited: false,
				fillable: false,
			},
			printers: [{ text: "Printer - Office #1A", printerId: 1 }],
			selectedPrinter: 1,
			allRowsSelected: false,
			headers: [
				{ text: "Style Code", value: "styleCode" },
				{ text: "Description", value: "description" },
				{ text: "Line #", value: "lineNumber" },
				{ text: "Ordered", value: "totalOrdered" },
				{ text: "Committed", value: "totalOrderQuantityCommitted" },
				{ text: "Available", value: "totalAvailable" },
				{ text: "Cust Order #", value: "orderNumber" },
				{ text: "Order Date", value: "orderDate" },
				{ text: "School/Organization", value: "organization" },
				{ text: "Status", value: "status" },
			],
			sortBy: null,
			sortDesc: true,
			showDetailsByRow: null,
			editAvailable: false,
			offset: 0,
			hasNextPage: false,
			hasPreviousPage: false,
			loading: true,
			loadingSizes: false,
			selectedItems: [],
			selectedStatus: null,
			showPickTicketPrintPreview: false,
			orderId: null,
			pickTicketIds: [],
		};
	},
	apollo: {
		fillableOrders: {
			query() {
				return gql`
					query GetFillableOrders(
						$offset: Int!
						$take: Int!
						$status: String!
						$search: String!
						$order: [FillableOrdersSortInput!]
					) {
						fillableOrders(
							skip: $offset
							take: $take
							order: $order
							where: {
								and: [{ status: { neq: "" } }, { status: { contains: $status } }]
								or: [
									{ styleCode: { contains: $search } }
									{ description: { contains: $search } }
									{ orderNumber: { contains: $search } }
									{ organization: { contains: $search } }
									{ status: { contains: $search } }
								]
							}
						) {
							items {
								salesOrderId
								systemCategoryId
								styleCode
								description
								lineItemId
								lineNumber
								orderNumber
								orderDate
								organization
								totalOrdered
								totalOrderQuantityCommitted
								totalAvailable
								status
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;
			},
			variables() {
				return {
					offset: this.offset,
					take: 15,
					status:
						this.searchForm.partialCommited && this.searchForm.fillable
							? ""
							: this.searchForm.partialCommited
							? "Partial Commit"
							: this.searchForm.fillable
							? "Fillable"
							: "",
					search: this.searchForm.keywords,
					order: this.sortBy
						? [{ [this.sortBy]: this.sortDesc ? "DESC" : "ASC" }]
						: [{ salesOrderId: "DESC" }],
				};
			},
			notifyOnNetworkStatusChange: true,
			update(data) {
				this.showDetailsByRow = null;
				this.hasNextPage = data.fillableOrders.pageInfo.hasNextPage;
				this.hasPreviousPage = data.fillableOrders.pageInfo.hasPreviousPage;

				data.fillableOrders.items.forEach(async (item) => {
					item.sizes = await this.getSizeInfo(item.salesOrderId, item.styleCode, null, item.lineItemId);
				});

				this.loading = false;

				return data.fillableOrders.items;
			},
		},
	},
	methods: {
		hasCommitPermissions() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Receiving_Commits_Commit_Decommit)) {
				return true;
			}
			return user.isAdmin;
		},
		hasPrintPermissions() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Receiving_Commits_PrintPickTicket)) {
				return true;
			}
			return user.isAdmin;
		},

		toggleSort(field) {
			if (this.sortBy !== field) {
				this.sortBy = field;
				this.sortDesc = false;
			} else if (!this.sortDesc) {
				this.sortDesc = true;
			} else {
				this.sortBy = null;
				this.sortDesc = true;
			}
			this.offset = 0;
		},

		formatDate(date) {
			if (!date) return "";

			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		selectAllRows() {
			//check if all rows are selected and select all if its not
			if (this.allRowsSelected) {
				this.fillableOrders = this.fillableOrders.map((obj) =>
					Object.assign({}, obj, { selected: false })
				);
				this.allRowsSelected = false;
			} else {
				this.fillableOrders = this.fillableOrders.map((obj) =>
					Object.assign({}, obj, { selected: true })
				);
				this.allRowsSelected = true;
			}

			//run through each order and deselect/select all the sizes
			this.fillableOrders.forEach((order) => this.selectAllSizes(order));
		},

		selectAllSizes(data, func) {
			if (func) func();

			data.sizes = data.sizes.map((size) => {
				this.$set(size, "selected", data.selected);
				return size;
			});
		},

		async getSizeInfo(salesOrderId, styleCode, sizeId = null, lineItemId = null) {
			try {
				this.loadingSizes = true;

				if (sizeId) {
					const res = await this.$apollo.query({
						query: gql`
							query GetFillableOrdersDetail($id: Int!, $styleCode: String!, $sizeId: Int!) {
								fillableOrdersDetails(
									take: 200
									where: {
										status: { neq: "" }
										salesOrderId: { eq: $id }
										styleCode: { eq: $styleCode }
										sizeId: { eq: $sizeId }
									}
								) {
									items {
										salesOrderId
										orderNumber
										orderDate
										lineItemId
										itemId
										stock
										systemCategoryId
										styleCode
										description
										sizeId
										size
										organization
										ordered
										orderQuantityCommitted
										available
										status
										pickTicketId
									}
								}
							}
						`,
						fetchPolicy: "no-cache",
						variables: {
							id: salesOrderId,
							styleCode: styleCode,
							sizeId: sizeId,
						},
					});

					return res.data.fillableOrdersDetails.items;
				}

				const res = await this.$apollo.query({
					query: gql`
						query GetFillableOrdersDetail($id: Int!, $styleCode: String!, $lineItemId: Long) {
							fillableOrdersDetails(
								take: 200
								where: {
									status: { neq: "" }
									salesOrderId: { eq: $id }
									styleCode: { eq: $styleCode }
									lineItemId: { eq: $lineItemId }
								}
							) {
								items {
									salesOrderId
									orderNumber
									orderDate
									lineItemId
									itemId
									stock
									systemCategoryId
									styleCode
									description
									sizeId
									size
									organization
									ordered
									orderQuantityCommitted
									available
									status
									pickTicketId
								}
							}
						}
					`,
					fetchPolicy: "no-cache",
					variables: {
						id: salesOrderId,
						styleCode: styleCode,
						lineItemId: lineItemId,
					},
				});

				return res.data.fillableOrdersDetails.items;
			} catch (error) {
				console.log(error);
				this.loadingSizes = false;
			}
		},

		async commitData(isCommit) {
			try {
				this.showDetailsByRow = null;
				this.loading = true;

				// Sort selectedItems by orderDate in ascending order
				this.selectedItems.sort((a, b) => new Date(a.orderDate) - new Date(b.orderDate));

				const body = {
					orders: this.selectedItems,
					isDecommit: !isCommit,
				};

				await this.$http.post(`/api/receiving/commit`, body);

				let ctx = this;
				setTimeout(function () {
					ctx.$apollo.queries.fillableOrders.refetch();
				}, 1000);
			} catch (error) {
				console.error(error);
				this.$apollo.queries.fillableOrders.refetch();
			}
		},

		renderStatusColor(statusLabel) {
			let statusColor;
			switch (statusLabel) {
				case "Not Shipped":
					statusColor = "table-status-yellow";
					break;
				case "Partial Commit":
					statusColor = "table-status-orange";
					break;
				case "No Commits":
					statusColor = "table-status-gray";
					break;
				case "Fillable":
					statusColor = "table-status-green";
					break;
				case "Partially Fillable":
					statusColor = "table-status-blue";
					break;
				case "Special Size":
					statusColor = "table-status-orange";
					break;
				default:
					statusColor = "";
			}

			return statusColor;
		},

		shouldDisableCheckbox(item, selectedStatus) {
			if (selectedStatus === "Not Shipped" && item.selected) {
				return false;
			} else if (selectedStatus === "Not Shipped" && item.salesOrderId === this.orderId) {
				return false;
			} else if (selectedStatus === "Not Shipped") {
				return true;
			}

			return selectedStatus && item.status !== selectedStatus;
		},
		updateSelection() {
			// Iterate through newOrders and update selectedItems accordingly
			const selectedItems = [];
			const pickTickets = new Set();

			this.fillableOrders.forEach((order) => {
				if (order.sizes && Array.isArray(order.sizes)) {
					order.sizes.forEach((size) => {
						if (size.selected) {
							selectedItems.push(size);

							if (size.pickTicketId) {
								pickTickets.add(size.pickTicketId);
							}
						}
					});
				}
			});

			// Update selectedItems
			this.selectedItems = selectedItems;
			this.selectedStatus =
				selectedItems.length > 0
					? this.fillableOrders.find((f) => f.salesOrderId === selectedItems[0].salesOrderId)
							?.status
					: null;

			this.orderId = selectedItems.length > 0 ? selectedItems[0].salesOrderId : null;
			this.pickTicketIds = Array.from(pickTickets);
		},
		async printPickTickets() {
			let promises = this.pickTicketIds.map(async (id) => {
				try {
					await this.printPickTicket(id);
				} catch (error) {
					console.log(error);
				}
			});
			await Promise.all(promises);
			this.showPickTicketPrintPreview = true;
		},
		async printPickTicket(id) {
			const res = await this.$http.post(`/api/orders/${this.orderId}/printPickTicket/${id}`);
			if (res.status != 200) {
				console.log(res);
			}
		},
	},
	watch: {
		fillableOrders: {
			deep: true,
			handler() {
				this.updateSelection();
			},
		},
		"searchForm.partialCommited": {
			handler(newPartialCommited) {
				// Filter fillableOrders based on newPartialCommited value
				if (newPartialCommited) {
					this.fillableOrders = this.fillableOrders.filter(
						(order) => order.status === "Partial Commit"
					);
				} else {
					// Reset the filter and refetch the data
					this.$apollo.queries.fillableOrders.refetch();
				}
			},
		},
		"searchForm.fillable": {
			handler(newFillable) {
				// Filter fillableOrders based on newFillable value
				if (newFillable) {
					this.fillableOrders = this.fillableOrders.filter((order) => order.status === "Fillable" || order.status === "Partially Fillable");
				} else {
					// Reset the filter and refetch the data
					this.$apollo.queries.fillableOrders.refetch();
				}
			},
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/commitsListState"];
					if (savedState) {
						this.offset = savedState.offset;
						this.searchForm.keywords = savedState.keywords;
						this.searchForm.partialCommited = savedState.partialCommited;
						this.searchForm.fillable = savedState.fillable;

						this.$nextTick(() => {
							if (typeof savedState.scrollPosition === "number") {
								setTimeout(() => {
									window.scrollTo({
										top: savedState.scrollPosition,
										left: 0,
										behavior: "smooth",
									});
								}, 500);
							}
						});
					}
				}
			},
			immediate: true,
		},
	},
	computed: {
		canPrint() {
			return this.selectedItems.every((item) => item.status === "Not Shipped");
		},
		canCommit() {
			return this.selectedItems.every(
				(item) =>
					(item.status === "Fillable" || item.available > 0) && item.status !== "Not Shipped"
			);
		},
		canDecommit() {
			return this.selectedItems.every((item) => item.orderQuantityCommitted > 0);
		},
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/saveCommitsListState", {
			offset: this.offset,
			keywords: this.searchForm.keywords,
			partialCommited: this.searchForm.partialCommited,
			fillable: this.searchForm.fillable,
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.table-status-yellow {
	border-color: #d5ce58;
	color: #d5ce58;
}

.commit-page {
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.commit-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;
}

.commit-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.commit-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.commit-table {
	border-radius: 0 0 4px 4px !important;
}
</style>

<style lang="scss" scoped>
.receiving {
	&-header {
		&-print {
			display: flex;
			justify-self: start;
			grid-column: 1;

			.form-input {
				margin: 0;
			}

			button {
				text-transform: initial;
				background-color: #ffffff;
				border-color: #bbbbbb;
				margin-left: 0.8rem;
			}
		}

		&-search {
			width: 100%;
			grid-column: 2;
		}

		&-actions {
			grid-column-start: 3;
			justify-self: end;
			display: flex;

			.v-btn {
				min-width: 13rem;
				text-transform: initial;
				background-color: $color-white;
				border-color: $color-gray;
				margin-left: 0.8rem;
			}
		}
	}

	&-table {
		position: relative;
		height: 0;
		overflow-y: auto;
		flex-grow: 1;
		background-color: $color-white;
		border-radius: 3px;

		a {
			color: $color-tertiary;
		}

		.form-input {
			margin: 0;
		}

		.flex-table {
			border: none;

			&[role="table-head"] {
				position: sticky;
				top: 0;
				z-index: 1;
			}

			&[role="table-body"] {
				.flex-table-row {
					border-top: none;
					border-bottom: solid 1px $color-gray;
				}
			}
		}

		.flex-table-cell {
			&:nth-child(1) {
				width: 4%;
				justify-content: center;
			}
			&:nth-child(2) {
				width: 12%;
			}
			&:nth-child(3) {
				width: 13%;
			}
			&:nth-child(4) {
				justify-content: center;
				width: 5%;
			}
			&:nth-child(5) {
				justify-content: center;
				width: 7%;
			}
			&:nth-child(6) {
				justify-content: center;
				width: 7%;
			}
			&:nth-child(7) {
				justify-content: center;
				width: 7%;
			}
			&:nth-child(8) {
				justify-content: center;
				width: 9%;
			}
			&:nth-child(9) {
				width: 9%;
			}
			&:nth-child(10) {
				width: 12%;
			}
			&:nth-child(11) {
				width: 9%;
			}
			&:nth-child(12) {
				justify-content: center;
				width: 4%;
			}

			&.sortable-header {
				cursor: pointer;
				user-select: none;

				.sort-icon {
					margin-left: 0.4rem;
					opacity: 0;
					transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
				}

				&:hover .sort-icon {
					opacity: 0.4;
				}

				&.sortable-active .sort-icon {
					opacity: 1;
				}

				&.sortable-desc .sort-icon {
					transform: rotate(180deg);
				}
			}
		}

		&-status {
			text-transform: uppercase; // statuses read as labels everywhere (#300)
			white-space: nowrap;
			display: inline-block;
			border: 1px solid;
			font-size: 1.4rem;
			padding: 0.5rem 1rem;
			border: 1px solid;

			&-yellow {
				border-color: #9acd32;
				color: #9acd32;
			}

			&-orange {
				border-color: $color-orange;
				color: $color-orange;
			}

			&-green {
				border-color: $color-green;
				color: $color-green;
			}

			&-blue {
				border-color: #1976D2;
				color: #1976D2;
			}
		}

		&-dropdown {
			flex-grow: 1;
			position: relative;
			max-height: 0;
			min-height: 0;
			border: none;
			overflow: hidden;

			// Animated by ExpandTransition; fit-content (not a fixed cap) so large
			// content is never squeezed into overlapping rows.
			&-active {
				max-height: fit-content;
			}
		}
	}

	&-size-chart {
		position: relative;
		background-color: $color-gray;
		padding: 4rem 25%;

		.flex-table {
			&[role="table-head"] {
				border: 1px solid $color-gray-dark;
				border-bottom: none;
				border-radius: 5px 5px 0 0;
				overflow: hidden;
			}

			&[role="table-body"] {
				border: 1px solid $color-gray-dark;
				border-bottom: none;
				background-color: $color-white;
				border-radius: 0 0 5px 5px;
				overflow: hidden;
			}
		}

		.flex-table-cell {
			&:nth-child(1) {
				justify-content: center;
				width: 7%;
			}
			&:nth-child(2) {
				justify-content: center;
				width: 18%;
			}
			&:nth-child(3) {
				justify-content: center;
				width: 18%;
			}
			&:nth-child(4) {
				justify-content: center;
				width: 18%;
			}
			&:nth-child(5) {
				justify-content: center;
				width: 20%;

				position: relative;
				padding-left: 3.5rem;
				padding-right: 3.5rem;

				.v-btn {
					position: absolute;
					right: 0;

					.v-icon {
						font-size: 1.8rem;
					}
				}
			}
			&:nth-child(6) {
				justify-content: flex-start;
				width: 19%;
			}

			.v-text-field {
				::v-deep input {
					text-align: center;
				}
			}
		}
	}
}

</style>
